import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };
const INPUT = 'w-full px-3 py-2 text-[13px] border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3fb5d2] focus:border-transparent text-[#3b3b3b] placeholder-[#c0c0c0] bg-white';
const LABEL = 'block text-[12px] font-medium text-[#646464] mb-1';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', permission_ids: [] });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesData, permissionsData] = await Promise.all([api.get('/api/roles'), api.get('/api/permissions')]);
      setRoles(rolesData);
      setPermissions(permissionsData);
    } catch (error) { alert('Gagal memuat data: ' + error.message); }
    finally { setLoading(false); }
  };

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({ name: role.name, description: role.description || '', permission_ids: role.permissions?.map(p => p.id) || [] });
    } else {
      setEditingRole(null);
      setFormData({ name: '', description: '', permission_ids: [] });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => { setShowModal(false); setEditingRole(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) await api.put(`/api/roles/${editingRole.id}`, formData);
      else await api.post('/api/roles', formData);
      handleCloseModal();
      loadData();
    } catch (error) { alert('Gagal menyimpan: ' + error.message); }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('Hapus role ini?')) return;
    try { await api.delete(`/api/roles/${roleId}`); loadData(); }
    catch (error) { alert('Gagal menghapus: ' + error.message); }
  };

  const loadRoleDetails = async (roleId) => {
    try { const data = await api.get(`/api/roles/${roleId}`); handleOpenModal(data); }
    catch (error) { alert('Gagal memuat: ' + error.message); }
  };

  const togglePermission = (id) => setFormData(p => ({
    ...p,
    permission_ids: p.permission_ids.includes(id) ? p.permission_ids.filter(x => x !== id) : [...p.permission_ids, id]
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Roles & Permissions</h1>
          <p className="text-[13px] text-[#8a8a8a] mt-0.5">Kelola role dan akses pengguna ke berbagai fitur.</p>
        </div>
        <button onClick={() => handleOpenModal()}
          className="flex items-center justify-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors w-full sm:w-auto flex-shrink-0">
          + Tambah Role
        </button>
      </div>

      <div className="bg-white rounded-2xl overflow-hidden" style={card}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#e5e5e5]">
                {['Nama Role', 'Deskripsi', 'Type', 'Actions'].map(h => (
                  <th key={h} className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider px-4 py-3 text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id} className="border-b border-[#e5e5e5] last:border-0 hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3 text-[13px] font-semibold text-[#3b3b3b] whitespace-nowrap">{role.name}</td>
                  <td className="px-4 py-3 text-[12px] text-[#8a8a8a]">{role.description || '—'}</td>
                  <td className="px-4 py-3">
                    {role.is_system_role ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">System</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-[#8a8a8a] border border-[#e5e5e5]">Custom</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => loadRoleDetails(role.id)} className="text-[12px] font-medium text-[#3fb5d2] hover:text-[#2a798c] mr-3 transition-colors">Edit</button>
                    {!role.is_system_role && (
                      <button onClick={() => handleDelete(role.id)} className="text-[12px] font-medium text-[#c62828] hover:text-red-700 transition-colors">Hapus</button>
                    )}
                  </td>
                </tr>
              ))}
              {roles.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-[13px] text-[#8a8a8a]">Belum ada role.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={card}>
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <h2 className="text-[16px] font-semibold text-[#3b3b3b] mb-5">{editingRole ? 'Edit Role' : 'Tambah Role Baru'}</h2>
                <div className="space-y-4">
                  <div>
                    <label className={LABEL}>Nama Role *</label>
                    <input type="text" required value={formData.name}
                      disabled={editingRole?.is_system_role}
                      onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                      className={INPUT + (editingRole?.is_system_role ? ' opacity-60 cursor-not-allowed' : '')} />
                  </div>
                  <div>
                    <label className={LABEL}>Deskripsi</label>
                    <textarea rows={3} value={formData.description}
                      onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                      className={INPUT + ' resize-none'} />
                  </div>
                  <div>
                    <label className={LABEL}>Permissions</label>
                    <div className="border border-[#e5e5e5] rounded-xl p-4 max-h-72 overflow-y-auto space-y-4">
                      {Object.entries(permissions).map(([moduleName, moduleData]) => (
                        <div key={moduleName}>
                          <h4 className="text-[12px] font-semibold text-[#646464] uppercase tracking-wide mb-2">{moduleData.module.display_name}</h4>
                          <div className="space-y-1.5 ml-2">
                            {moduleData.permissions.map((perm) => (
                              <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox"
                                  checked={formData.permission_ids.includes(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                  disabled={editingRole?.is_system_role && editingRole.name === 'admin'}
                                  className="h-4 w-4 rounded text-[#3fb5d2] border-[#e5e5e5] focus:ring-[#3fb5d2]" />
                                <span className="text-[12px] text-[#646464]">{perm.display_name} <span className="text-[#8a8a8a]">({perm.action})</span></span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-[#f5f5f5] rounded-b-2xl flex justify-end gap-3">
                <button type="button" onClick={handleCloseModal}
                  className="px-4 py-2 text-[13px] font-medium text-[#646464] border border-[#e5e5e5] rounded-lg bg-white hover:bg-gray-50 transition-colors">
                  Batal
                </button>
                <button type="submit"
                  className="px-4 py-2 text-[13px] font-semibold bg-[#3fb5d2] hover:bg-[#2a798c] text-white rounded-lg transition-colors">
                  {editingRole ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;
